import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Divider,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Spin,
  Switch,
  Typography,
  message,
} from 'antd';
import {
  ApartmentOutlined,
  ReloadOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import lib from '../../assets/utils/lib';
import { useAuth } from '../../assets/utils/useAuth';
import { usePermissions } from '../../assets/utils/usePermissions';
import './notification-preferences.css';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const WRITE_PERMS = ['admin.*', 'organizations.manage', 'corporations.manage', 'plans.manage'];

function canWriteEnrollmentSegments(user, hasAnyPermission) {
  if (typeof hasAnyPermission === 'function' && hasAnyPermission(WRITE_PERMS)) return true;
  const perms = user?.profile?.permissions;
  if (!Array.isArray(perms) || !perms.length) return false;
  if (perms.includes('admin.*')) return true;
  return WRITE_PERMS.some((p) => p !== 'admin.*' && perms.includes(p));
}

function normalizeSegmentRow(row = {}) {
  const criteria = row.criteria && typeof row.criteria === 'object' ? row.criteria : {};
  const active = criteria.active_enrollees || {};
  const declared = criteria.declared_headcount || {};
  const policies = criteria.active_policies || {};
  return {
    key: row.key || row.segment_key,
    label: row.label || '',
    description: row.description || '',
    sort_order: row.sort_order != null ? Number(row.sort_order) : 0,
    is_default: Boolean(row.is_default),
    summary: criteria.summary || '',
    match_mode: criteria.match_mode === 'any' ? 'any' : 'all',
    active_enrollees_min: active.min != null && active.min !== '' ? Number(active.min) : null,
    active_enrollees_max: active.max != null && active.max !== '' ? Number(active.max) : null,
    declared_headcount_min: declared.min != null && declared.min !== '' ? Number(declared.min) : null,
    declared_headcount_max: declared.max != null && declared.max !== '' ? Number(declared.max) : null,
    active_policies_min: policies.min != null && policies.min !== '' ? Number(policies.min) : null,
    active_policies_max: policies.max != null && policies.max !== '' ? Number(policies.max) : null,
  };
}

function buildPatchPayload(values) {
  const range = (min, max) => {
    const out = {};
    if (min != null && min !== '') out.min = Number(min);
    if (max != null && max !== '') out.max = Number(max);
    return Object.keys(out).length ? out : undefined;
  };
  return {
    label: values.label,
    description: values.description || '',
    sort_order: values.sort_order != null ? Number(values.sort_order) : 0,
    is_default: Boolean(values.is_default),
    criteria: {
      summary: values.summary || '',
      match_mode: values.match_mode === 'any' ? 'any' : 'all',
      ...(range(values.active_enrollees_min, values.active_enrollees_max)
        ? { active_enrollees: range(values.active_enrollees_min, values.active_enrollees_max) }
        : {}),
      ...(range(values.declared_headcount_min, values.declared_headcount_max)
        ? { declared_headcount: range(values.declared_headcount_min, values.declared_headcount_max) }
        : {}),
      ...(range(values.active_policies_min, values.active_policies_max)
        ? { active_policies: range(values.active_policies_min, values.active_policies_max) }
        : {}),
    },
  };
}

const MetricRange = ({ minName, maxName, label, disabled }) => (
  <Form.Item label={label} style={{ marginBottom: 8 }}>
    <Space>
      <Form.Item name={minName} noStyle>
        <InputNumber placeholder="Min" min={0} disabled={disabled} style={{ width: 110 }} />
      </Form.Item>
      <Text type="secondary">to</Text>
      <Form.Item name={maxName} noStyle>
        <InputNumber placeholder="Max" min={0} disabled={disabled} style={{ width: 110 }} />
      </Form.Item>
    </Space>
  </Form.Item>
);

const SegmentEditor = ({ segment, canWrite, savingKey, onSave }) => {
  const [form] = Form.useForm();
  const key = segment.key;

  useEffect(() => {
    form.setFieldsValue(segment);
  }, [form, segment]);

  const handleSave = async () => {
    const values = await form.validateFields().catch(() => null);
    if (!values) return;
    onSave(key, buildPatchPayload(values));
  };

  return (
    <Card
      size="small"
      title={(
        <Space>
          <Text strong>{segment.label || key}</Text>
          <Text type="secondary" code>{key}</Text>
          {segment.is_default ? <Text type="secondary">(default)</Text> : null}
        </Space>
      )}
      extra={canWrite ? (
        <Button
          type="primary"
          size="small"
          icon={<SaveOutlined />}
          loading={savingKey === key}
          onClick={handleSave}
        >
          Save
        </Button>
      ) : null}
    >
      <Form form={form} layout="vertical" disabled={!canWrite}>
        <Form.Item name="label" label="Label" rules={[{ required: true, message: 'Label required' }]}>
          <Input />
        </Form.Item>
        <Form.Item name="description" label="Description">
          <TextArea rows={2} />
        </Form.Item>
        <Form.Item name="summary" label="AM-facing summary">
          <TextArea rows={2} placeholder="Shown as guidance for account managers" />
        </Form.Item>
        <Space wrap align="start" size="large">
          <Form.Item name="match_mode" label="Match mode" style={{ minWidth: 160 }}>
            <Select
              options={[
                { value: 'all', label: 'All metrics (AND)' },
                { value: 'any', label: 'Any metric (OR)' },
              ]}
            />
          </Form.Item>
          <Form.Item name="sort_order" label="Sort order">
            <InputNumber min={0} style={{ width: 100 }} />
          </Form.Item>
          <Form.Item name="is_default" label="Default segment" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Space>
        <Divider style={{ margin: '8px 0 16px' }} />
        <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>Classification criteria</Text>
        <MetricRange minName="active_enrollees_min" maxName="active_enrollees_max" label="Active enrollees" disabled={!canWrite} />
        <MetricRange minName="declared_headcount_min" maxName="declared_headcount_max" label="Declared headcount" disabled={!canWrite} />
        <MetricRange minName="active_policies_min" maxName="active_policies_max" label="Active policies (optional)" disabled={!canWrite} />
      </Form>
    </Card>
  );
};

const EnrollmentSegmentsSettings = ({ embedded = false }) => {
  const { user } = useAuth();
  const { hasAnyPermission } = usePermissions();
  const canWrite = useMemo(
    () => canWriteEnrollmentSegments(user, hasAnyPermission),
    [user, hasAnyPermission],
  );

  const [taxonomy, setTaxonomy] = useState(null);
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [taxParsed, listParsed] = await Promise.all([
        lib.parseApiEnvelope(await lib.getOrgEnrollmentSegmentsTaxonomy()),
        lib.parseApiEnvelope(await lib.getOrgEnrollmentSegments()),
      ]);
      if (!listParsed.ok) {
        setError(listParsed.message || 'Failed to load enrollment segments');
        setSegments([]);
        return;
      }
      if (taxParsed.ok) setTaxonomy(taxParsed.data);
      const rows = Array.isArray(listParsed.data)
        ? listParsed.data
        : (listParsed.data?.segments || listParsed.data?.items || []);
      setSegments(rows.map(normalizeSegmentRow).filter((s) => s.key));
    } catch (e) {
      setError(e?.message || 'Failed to load enrollment segments');
      setSegments([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async (key, payload) => {
    setSavingKey(key);
    try {
      const parsed = lib.parseApiEnvelope(await lib.updateOrgEnrollmentSegment(key, payload));
      if (parsed.ok) {
        message.success(parsed.message || `${key} saved`);
        await load();
      } else {
        message.error(parsed.message || 'Save failed');
      }
    } catch (e) {
      message.error(e?.message || 'Save failed');
    } finally {
      setSavingKey(null);
    }
  };

  const body = (
    <Spin spinning={loading}>
      {!embedded ? (
        <div className="preferences-header">
          <div>
            <Title level={3} style={{ margin: 0 }}>
              <ApartmentOutlined style={{ marginRight: 8 }} />
              Enrollment segments
            </Title>
            <Text type="secondary">
              Define corporate vs SME classification rules used for plan assignment
            </Text>
          </div>
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
            Refresh
          </Button>
        </div>
      ) : (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, gap: 12 }}>
          <div>
            <Text strong style={{ display: 'block' }}>Corporate vs SME classification</Text>
            <Text type="secondary" style={{ fontSize: 13 }}>
              These rules decide which plan catalog segment an organization uses. Tag plans in Catalog; override on the org profile when needed.
            </Text>
          </div>
          <Button size="small" icon={<ReloadOutlined />} onClick={load} loading={loading}>
            Refresh
          </Button>
        </div>
      )}

      {!canWrite ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Read-only"
          description="You need organizations.manage, corporations.manage, or plans.manage to edit segment definitions."
        />
      ) : null}

      {taxonomy?.classification_policy || taxonomy?.classification_policy_note ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={taxonomy.classification_policy
            ? `Policy: ${String(taxonomy.classification_policy).replace(/_/g, ' ')}`
            : 'Classification policy'}
          description={taxonomy.classification_policy_note
            || 'Crossing a threshold mid-cycle does not change the effective segment until the next org renewal. Manual overrides apply immediately.'}
        />
      ) : (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Cycle-boundary classification"
          description="Computed segment updates at org renewal. Account managers can override on the org profile immediately."
        />
      )}

      {error ? (
        <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />
      ) : null}

      {!embedded ? (
        <Paragraph type="secondary" style={{ marginBottom: 16 }}>
          Plans are tagged with segments in the plan catalog. Orgs get an effective segment
          (override or cycle-locked). Manage plans and enrollment require a matching plan segment.
        </Paragraph>
      ) : null}

      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {segments.map((segment) => (
          <SegmentEditor
            key={segment.key}
            segment={segment}
            canWrite={canWrite}
            savingKey={savingKey}
            onSave={handleSave}
          />
        ))}
        {!loading && !segments.length && !error ? (
          <Alert type="warning" showIcon message="No enrollment segments returned by the API." />
        ) : null}
      </Space>
    </Spin>
  );

  if (embedded) {
    return <div className="enrollment-segments-embedded">{body}</div>;
  }

  return (
    <div className="notification-preferences">
      <Card>{body}</Card>
    </div>
  );
};

export default EnrollmentSegmentsSettings;
